import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/utils";
import { MoreHorizontal } from "lucide-react";
import React, { useState } from "react";

/** Shared hover styling for items inside a RowKebab menu. */
export const rowKebabMenuItemClassName =
  "rounded-lg px-3 py-2 hover:bg-n-95/8 dark:hover:bg-n-95/10 focus:bg-n-95/8 dark:focus:bg-n-95/10";

interface RowKebabProps {
  children: React.ReactNode;
  /** Radix modality; rows with dwell previews pass false. */
  modal?: boolean;
  /** Mirrors the menu's open state to the host row — rows that swap other
   *  content out for the actions cluster must hold the swap while the menu
   *  is open (the pointer leaves the row for the portaled menu). */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Hover-revealed ⋯ menu for list rows. The trigger stays invisible until the
 * enclosing `group` is hovered or the menu is open.
 */
export function RowKebab({
  children,
  modal = true,
  onOpenChange,
}: RowKebabProps) {
  const [open, setOpen] = useState(false);
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };
  return (
    <DropdownMenu modal={modal} open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-7 w-7 rounded-lg text-muted-foreground transition-opacity",
            "hover:text-foreground hover:bg-n-95/8 dark:hover:bg-n-95/10",
            open ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-[17px] w-[17px]" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        className="w-44 bg-background/95 backdrop-blur-lg border rounded-xl shadow-xl shadow-black/10"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
